import { get, head, defaultTo } from 'lodash';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION } from 'constants/common';
import { REDUCER } from 'constants/entity-names';
import { getStore } from 'store';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc CallDispositionReasons defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { CallDispositionReasonsService } from 'services/callDispositionReasons';
 *
 * // create service instance and send/cancel request
 * const callDispositionReasonsService = new CallDispositionReasonsService();
 * const result = await callDispositionReasonsService.fetchData( '<user-id>' ); // fetch data
 * callDispositionReasonsService.cancel(); // cancel an active request
 */
export class CallDispositionReasonsService extends HttpService {

    // fetch end call widget data
    async fetchComponentData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        // extract data
        const { memberId, patientId } = data;
        
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        operation: CRUD_OPERATION.READ,
                        memberId,
                        patientId
                    },
                }
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    items: get( response, 'body.component.data.state.items', [] ),
                    options: get( response, 'body.component.data.options.categoryAndReason', [] ),
                    actions: get( response, 'body.component.structure.actions', [] ),
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // add or update category/reason item
    async addUpdateItem( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        // extract `operation` and actual payload
        const { operation, id = null, category, reason, memberId } = data;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        operation,
                        memberId
                    },
                    data: {
                        id,
                        category,
                        reason,
                    },
                }
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    id: defaultTo( head( get( response, 'body.component.data.itemIds', [] ) ), id ),
                    category,
                    reason,
                    description: get( response, 'body.component.data.description', undefined )
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // delete a category/reason item
    async deleteItem( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        // extract `operation` and actual payload
        const { id, memberId } = data;


        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        operation: CRUD_OPERATION.DELETE,
                        memberId
                    },
                    data: {
                        id
                    }
                }
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: null,
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }
}
